import {
  type SanityBundleDiscount,
  type SanityDiscount,
  type SanityDiscountProduct,
  type SanityDiscountType,
  type SanityQuantityDiscount,
} from '@data/sanity/queries/types/site'
import { type LineItem, type VariantLineItem } from './cart/types'
import { compareNumbers } from './helpers'
import { sanityProductIdToShopifyId } from './product'

type ValueCounts = Record<string, number>

export interface QuantityDiscount {
  id: string
  title: string
  discountValuePercent: number
  minimumQuantity: number
  maximumQuantity: number
}

export interface BundleDiscount {
  id: string
  title: string
  discountValuePercent: number
  productQuantity: number
  doNotStackWithQuantityDiscounts: boolean
  variantGroups: string[]
}

type BundleDiscountWithVariants = BundleDiscount & {
  variantIds: number[]
}

export interface DiscountItem {
  id: string
  type: SanityDiscountType
  title: string
  amount: number
  quantity: number
  discountValuePercent: number
}

/**
 * Gets quantity discounts from all discounts.
 */
export const getQuantityDiscounts = (
  discounts: SanityDiscount[],
): QuantityDiscount[] =>
  (
    discounts.filter(
      (discount) => discount.type === 'quantity',
    ) as SanityQuantityDiscount[]
  ).map(
    ({
      _id,
      title,
      discountValuePercent,
      minimumQuantity,
      maximumQuantity,
    }) => ({
      id: _id,
      title,
      discountValuePercent,
      minimumQuantity,
      maximumQuantity,
    }),
  )

/**
 * Gets bundle discounts from all discounts.
 */
export const getBundleDiscounts = (
  discounts: SanityDiscount[],
): BundleDiscount[] =>
  (
    discounts.filter(
      (discount) => discount.type === 'bundle',
    ) as SanityBundleDiscount[]
  ).map((discount) => ({
    id: discount._id,
    title: discount.title,
    discountValuePercent: discount.discountValuePercent,
    productQuantity: discount.products?.length ?? 0,
    doNotStackWithQuantityDiscounts: !!discount.doNotStackWithQuantityDiscounts,
    variantGroups: getBundleDiscountVariantGroups(discount.products ?? []),
  }))

/**
 * Finds active quantity discount by quantity.
 */
const getActiveQuantityDiscount = (
  quantityDiscounts: QuantityDiscount[],
  quantity: number,
): QuantityDiscount | null =>
  quantityDiscounts
    .filter(
      (quantityDiscount) =>
        !quantityDiscount.minimumQuantity ||
        quantity >= quantityDiscount.minimumQuantity,
    )
    .filter(
      (quantityDiscount) =>
        !quantityDiscount.maximumQuantity ||
        quantity <= quantityDiscount.maximumQuantity,
    )
    // Get the discount with the highest discount value
    .reduce<QuantityDiscount | null>(
      (maximumDiscount, discount) =>
        maximumDiscount &&
        maximumDiscount.discountValuePercent > discount.discountValuePercent
          ? maximumDiscount
          : discount,
      null,
    )

/**
 * Gets groups of variant IDs joined in a string (e.g., ["v1,v2", "v1,v3", ...]).
 */
const getBundleDiscountVariantGroups = (
  products: SanityDiscountProduct[],
): string[] => {
  // Get variant IDs grouped by products
  const variantsByProduct: number[][] = []
  products.forEach((product) => {
    const variants: number[] = []

    product.variantIds.forEach((variantId) => {
      const productVariantShopifyId = sanityProductIdToShopifyId(variantId)

      if (productVariantShopifyId) {
        variants.push(productVariantShopifyId)
      }
    })

    variantsByProduct.push(variants)
  })

  // Create a group of all possible variant mappings
  const productQuantity = products.length ?? 0
  const getVariantGroups = (productIndex = 0): number[][] => {
    const productVariants = variantsByProduct[productIndex] ?? []

    if (productIndex >= productQuantity - 1) {
      return productVariants.map((variantId) => [variantId])
    }

    const result: number[][] = []
    const variantGroups = getVariantGroups(productIndex + 1)

    productVariants.forEach((variantId) => {
      variantGroups.forEach((variantIds) => {
        result.push([variantId, ...variantIds])
      })
    })

    return result
  }

  // Sort and merge variant IDs in the group
  return getVariantGroups().map((variantIds) =>
    variantIds.sort(compareNumbers).join(','),
  )
}

/**
 * Gets all possible variant variations with given quantity of items.
 */
const getAllVariantGroups = (
  unusedVariantIdCounts: ValueCounts,
  quantity: number,
): string[] => {
  const getVariantGroups = (
    availableValueCounts: ValueCounts,
    values: number[] = [],
    index = 0,
  ): number[][] => {
    const availableValues = Object.entries(availableValueCounts)
      .filter((availableValueCount) => availableValueCount[1] > 0)
      .map((availableValueCount) => Number(availableValueCount[0]))

    // Check if this is the last index
    if (index >= quantity - 1) {
      const results: number[][] = []

      availableValues.forEach((value) => results.push([value]))

      return results
    }

    const mergedResults: number[][] = []

    availableValues.forEach((value) => {
      const newAvailableValueCounts = { ...availableValueCounts }
      const newValues = [...values]

      newAvailableValueCounts[`${value}`] -= 1
      newValues.push(value)

      getVariantGroups(newAvailableValueCounts, newValues, index + 1).forEach(
        (valueVariation) => mergedResults.push([value, ...valueVariation]),
      )
    })

    return mergedResults
  }

  // Merge values into a string, filter only unique results
  return getVariantGroups(unusedVariantIdCounts)
    .map((values) => values.sort(compareNumbers).join(','))
    .filter((value, index, array) => array.indexOf(value) === index)
}

/**
 * Gets value counts from an array of values (e.g., { item1: 3, item2: 1, ... }).
 */
function getValueCounts<T = string | number>(values: T[]) {
  const valueCounts: ValueCounts = {}

  values.forEach((value) => {
    valueCounts[`${value}`] = (valueCounts?.[`${value}`] ?? 0) + 1
  })

  return valueCounts
}

/**
 * .
 */
const getMatchingBundleDiscount =
  (
    allVariantGroups: Record<number, string[]>,
    unusedVariantIdCounts: ValueCounts,
  ) =>
  (bundleDiscount: BundleDiscount) => {
    const quantity = bundleDiscount.productQuantity

    if (!allVariantGroups[quantity]) {
      allVariantGroups[quantity] = getAllVariantGroups(
        unusedVariantIdCounts,
        quantity,
      )
    }

    // Check if bundle contains any of the possible variant variations
    const matchingVariantGroup = allVariantGroups[quantity].find(
      (variantGroup) => bundleDiscount.variantGroups.includes(variantGroup),
    )

    if (!matchingVariantGroup) {
      return null
    }

    return {
      ...bundleDiscount,
      variantIds: matchingVariantGroup
        .split(',')
        .map((variantId) => Number(variantId)),
    }
  }

/**
 * Gets discount amounts for bundle discounts.
 */
const getBundleDiscountAmounts =
  (variantLineItems: VariantLineItem[]) =>
  (matchedBundleDiscount: BundleDiscountWithVariants) => {
    // Get variant line items from bundle variant IDs
    const matchedVariantLineItems = matchedBundleDiscount.variantIds
      .map((variantId) =>
        variantLineItems.find(
          (variantLineItem) => variantLineItem.id === variantId,
        ),
      )
      .filter(Boolean) as VariantLineItem[]

    const totalPrice = matchedVariantLineItems.reduce(
      (total, { price }) => total + price,
      0,
    )

    return {
      id: matchedBundleDiscount.id,
      amount: totalPrice * (matchedBundleDiscount.discountValuePercent / 100),
    }
  }

/**
 * Gets the next active bundle discount.
 */
const getNextActiveBundleDiscount = (
  variantLineItems: VariantLineItem[],
  bundleDiscounts: BundleDiscount[],
  unusedVariantIds: number[],
): BundleDiscountWithVariants | null => {
  const minimumProductQuantity = Math.min(
    ...bundleDiscounts.map((bundleDiscount) => bundleDiscount.productQuantity),
  )

  // Check if there are not enough unused variants to potentially form a bundle
  if (unusedVariantIds.length < minimumProductQuantity) {
    return null
  }

  // Find all possible bundle discounts from given variants
  const allVariantGroups: Record<number, string[]> = {}
  const unusedVariantIdCounts = getValueCounts(unusedVariantIds)
  const matchedBundleDiscounts = bundleDiscounts
    .map(getMatchingBundleDiscount(allVariantGroups, unusedVariantIdCounts))
    .filter(Boolean) as BundleDiscountWithVariants[]

  if (matchedBundleDiscounts.length < 2) {
    return matchedBundleDiscounts[0] ?? null
  }

  // Calculate discount amounts for found bundle discounts and sort them by amount in descending order
  const discountAmounts = matchedBundleDiscounts.map(
    getBundleDiscountAmounts(variantLineItems),
  )
  discountAmounts.sort(
    (discountAmount1, discountAmount2) =>
      discountAmount2.amount - discountAmount1.amount,
  )

  // Return the bundle with the largest discount amount
  return (
    matchedBundleDiscounts.find(
      (discount) => discount.id === discountAmounts[0].id,
    ) ?? matchedBundleDiscounts[0]
  )
}

/**
 * Gets all active bundle discounts for given variant IDs.
 */
const getActiveBundleDiscounts = (
  variantLineItems: VariantLineItem[],
  bundleDiscounts: BundleDiscount[],
  limit = 1000,
): BundleDiscountWithVariants[] => {
  const variantIds = variantLineItems
    .map((variantLineItem) =>
      new Array(variantLineItem.quantity)
        .fill(null)
        .map(() => variantLineItem.id),
    )
    .flat(1)
  const unusedVariantIds = [...variantIds]
  const activeBundleDiscounts: BundleDiscountWithVariants[] = []
  let count = 0

  // Find the next active bundle discount
  let activeBundleDiscount = getNextActiveBundleDiscount(
    variantLineItems,
    bundleDiscounts,
    unusedVariantIds,
  )

  while (activeBundleDiscount) {
    activeBundleDiscounts.push(activeBundleDiscount)

    // Remove variant IDs from unused variants
    activeBundleDiscount.variantIds.forEach((variantId) => {
      const index = unusedVariantIds.indexOf(variantId)

      if (index > -1) {
        unusedVariantIds.splice(index, 1)
      }
    })

    // Limit the total count
    count += 1

    if (count >= limit) {
      break
    }

    // Find the next active bundle discount
    activeBundleDiscount = getNextActiveBundleDiscount(
      variantLineItems,
      bundleDiscounts,
      unusedVariantIds,
    )
  }

  return activeBundleDiscounts
}

/**
 * Gets quantity discount items for variant line items.
 */
const getQuantityDiscountItems = (
  variantLineItems: VariantLineItem[],
  quantityDiscounts: QuantityDiscount[],
): DiscountItem[] => {
  const totalQuantity = variantLineItems.reduce(
    (total, { quantity }) => total + quantity,
    0,
  )
  const activeQuantityDiscount = getActiveQuantityDiscount(
    quantityDiscounts,
    totalQuantity,
  )

  if (!activeQuantityDiscount) {
    return []
  }

  const totalPrice = variantLineItems.reduce(
    (total, { price, quantity }) => total + price * quantity,
    0,
  )

  return [
    {
      id: activeQuantityDiscount.id,
      type: 'quantity',
      title: activeQuantityDiscount.title,
      amount: totalPrice * (activeQuantityDiscount.discountValuePercent / 100),
      quantity: 1,
      discountValuePercent: activeQuantityDiscount.discountValuePercent,
    },
  ]
}

/**
 * Gets bundle discount items for variant line items.
 */
const getBundleDiscountItems = (
  variantLineItems: VariantLineItem[],
  bundleDiscounts: BundleDiscount[],
): DiscountItem[] => {
  const discountItems: DiscountItem[] = []

  // Get active bundle discounts
  const activeBundleDiscounts = getActiveBundleDiscounts(
    variantLineItems,
    bundleDiscounts,
  )

  activeBundleDiscounts.forEach((activeBundleDiscount) => {
    // Do not add duplicate bundles (use quantity instead)
    if (
      discountItems.some(
        (discountItem) => discountItem.id === activeBundleDiscount.id,
      )
    ) {
      return
    }

    // Get the total price of bundle variants
    const bundleItemPrice = variantLineItems
      .filter((variantLineItem) =>
        activeBundleDiscount.variantIds.includes(variantLineItem.id),
      )
      .reduce((total, { price }) => total + price, 0)
    const discountAmount =
      bundleItemPrice * (activeBundleDiscount.discountValuePercent / 100)

    const discountQuantity = activeBundleDiscounts.filter(
      (discount) => discount.id === activeBundleDiscount.id,
    ).length

    discountItems.push({
      id: activeBundleDiscount.id,
      type: 'bundle',
      title: activeBundleDiscount.title,
      amount: discountAmount,
      quantity: discountQuantity,
      discountValuePercent: activeBundleDiscount.discountValuePercent,
    })
  })

  return discountItems
}

/**
 * Gets discounts items for that apply to given variants.
 */
export const getDiscountItems = (
  variantLineItems: VariantLineItem[],
  quantityDiscounts: QuantityDiscount[],
  bundleDiscounts: BundleDiscount[],
) => {
  // Quantity discount
  const quantityDiscountItems = getQuantityDiscountItems(
    variantLineItems,
    quantityDiscounts,
  )

  // Bundle discount
  const filteredBundleDiscounts = bundleDiscounts.filter(
    (bundleDiscount) =>
      // Filter bundle discounts that do not stack with quantity discounts
      !bundleDiscount.doNotStackWithQuantityDiscounts ||
      quantityDiscountItems.length === 0,
  )
  const bundleDiscountItems = getBundleDiscountItems(
    variantLineItems,
    filteredBundleDiscounts,
  )

  return [...quantityDiscountItems, ...bundleDiscountItems]
}

/**
 * Gets discount items that apply to the cart.
 */
export const getCartDiscountItems = (
  lineItems: LineItem[],
  quantityDiscounts: QuantityDiscount[],
  bundleDiscounts: BundleDiscount[],
): DiscountItem[] => {
  const variantLineItems: VariantLineItem[] = lineItems.map(
    (lineItem) =>
      ({
        id: lineItem.variantID,
        price: lineItem.price,
        quantity: lineItem.quantity,
      }) satisfies VariantLineItem,
  )

  return getDiscountItems(variantLineItems, quantityDiscounts, bundleDiscounts)
}
